<svelte:options />

<script>
    import { run } from 'svelte/legacy';
    import BookCollectionAccordianSection from './BookCollectionAccordianSection.svelte';
    import { lastDocumentViewerItem } from '@repo/stores/Actions';
    import ResourceHub from './resource/ResourceHub.svelte';
    import './book-accordion.css';
    import { currentBreadcrumb } from '@repo/stores/NavigationStore';
    import { collection, doc, getDocs } from 'firebase/firestore';
    import { COLLECTION_TYPES } from '@repo/content-db';
    import { resolveStorageUrl } from '@repo/config/Storage';
    import { fade } from 'svelte/transition';
    import { onMount, createEventDispatcher } from 'svelte';
    import { postHogCapture } from '@repo/config/postHogHelper';
    import { getBookAccordionDeps } from './context.js';

    const { access, db } = getBookAccordionDeps();
    const { canAccessTeacherOnlyMaterial, canAccessResource, hasBookAccess = () => false } = access;
    const { getEntity, getFirestore, contentDb } = db;
    /**
     * @typedef {Object} Props
     * @property {any} data
     * @property {any} activeItem
     * @property {any} [level]
     * @property {Set<string> | null} [currentHiddenIds]
     * @property {boolean} [parentHidden]
     */
    let { data, activeItem, level = null, currentHiddenIds = null, parentHidden = false } = $props();

    const dispatch = createEventDispatcher();

    let showSubItems = $state(false);
    
    let subItems = $state([]);
    
    let lastActiveItem = $state(0);
    
    let titles = $state({});
    
    let docPathFetchAttempted = $state(false);
    

    run(() => {
        if (activeItem) {
            if (activeItem !== data.id) {
                showSubItems = false;
            }
        }
    });

    let hasAccess = $state(false);

    async function getSubItems() {
        subItems = [];
        const canAccessTeacherOnlyContent = canAccessTeacherOnlyMaterial();
        const bookId = data._collectionId || data.id;

        try {
            hasAccess = hasBookAccess(bookId);
            if (!hasAccess && !data._collectionId) {
                hasAccess = await canAccessResource(data.id);
            }

            // Create an array of promises for fetching items
            const fetchPromises = data.items.map(async (i) => {
                const item = await Promise.any([
                    getEntity({ id: i }, COLLECTION_TYPES.COLLECTIONS),
                    getEntity({ id: i }, COLLECTION_TYPES.COURSES),
                    getEntity({ id: i }, COLLECTION_TYPES.RESOURCES),
                ]).catch(() => null);

                if (item && item.exists()) {
                    const d = item.data();
                    d.id = item.id;
                    d._collection = item.metadata.collection;
                    d._collectionId = d._collectionId || bookId;

                    const intendedEndUserRole = d.intendedEndUserRole || null;
                    if (
                        intendedEndUserRole !== 'Teacher' ||
                        canAccessTeacherOnlyContent
                    ) {
                        return d;
                    }
                }
                return null; // Return null for items that don't meet the conditions
            });

            // Await all promises concurrently
            const fetchResults = await Promise.allSettled(fetchPromises);

            // Handle the fetch results
            const results = fetchResults.map((result) => {
                if (result.status === 'fulfilled') {
                    return result.value;
                }

                return null;
            });

            // Filter out null results and update subItems
            subItems = results.filter((item) => item !== null);
        } catch (error) {
            console.error(error);
        }
    }

    async function getSubItemsFromDocPath() {
        if (!data?.docPath) return;
        const pathSegments = data.docPath
            .split('/')
            .filter((segment) => segment.length > 0);
        if (pathSegments.length === 0) return;

        docPathFetchAttempted = true;
        subItems = [];

        try {
            const canAccessTeacherOnlyContent = canAccessTeacherOnlyMaterial();
            // `docPath` always points at `read-only-titles/...` which lives
            // in content-db. Fall back to getFirestore() only if the host
            // app hasn't wired contentDb yet.
            const dbRef = contentDb || getFirestore();
            const docRef = doc(dbRef, ...pathSegments);
            const childrenSnap = await getDocs(collection(docRef, 'items'));

            const bookId = data._collectionId || data.id;
            const fetchedItems = childrenSnap.docs
                .map((child) => {
                    const childData = child.data();
                    childData.id = child.id;
                    childData.docPath = child.ref.path;
                    childData._collectionId = childData._collectionId || bookId;
                    const metadataCollection = child.metadata?.collection;
                    if (metadataCollection) childData._collection = metadataCollection;
                    return childData;
                })
                .filter((childData) => {
                    const intendedEndUserRole = childData.intendedEndUserRole || null;
                    return intendedEndUserRole !== 'Teacher' || canAccessTeacherOnlyContent;
                })
                .sort(sortChildItems);

            hasAccess = hasBookAccess(bookId);
            if (!hasAccess && !data._collectionId) {
                hasAccess = await canAccessResource(data.id);
            }
            subItems = fetchedItems;
        } catch (error) {
            console.error(error);
            docPathFetchAttempted = false;
        }
    }

    lastDocumentViewerItem.subscribe((i) => {
        if (i && data && data.items && i.id === data.id) {
            lastActiveItem = i.id;
            if (document.getElementById('el_' + i.id))
                document
                    .getElementById('el_' + i.id)
                    .scrollIntoView({ behavior: 'smooth' });
            showSubItems = true;
        }
    });

    function toggle() {
        showSubItems = !showSubItems;
        if (showSubItems) open();

        dispatch('toggle', {
            itemId: data.id,
        });

        postHogCapture('library_accordian_toggle_item', { itemId: data.id });
    }

    function open() {
        showSubItems = true;
        if (data.items && subItems && subItems.length !== data.items.length) {
            // Check if data.items is an array of IDs or objects
            if (Array.isArray(data.items) && data.items.length > 0) {
                if (typeof data.items[0] === 'string') {
                    getSubItems();
                } else if (typeof data.items[0] === 'object' && data.items[0] !== null) {
                    const bookId = data._collectionId || data.id;
                    hasAccess = hasBookAccess(bookId);
                    if (!hasAccess && !data._collectionId) {
                        canAccessResource(data.id).then(res => { hasAccess = res; });
                    }
                    // If they are already objects, assign them directly
                    subItems = data.items;
                }
            }
        } else if (
            (!data.items || data.items.length === 0) &&
            data.docPath &&
            !docPathFetchAttempted
        ) {
            getSubItemsFromDocPath();
        }
    }

    function formatSortNumber(m) {
        return m.padStart(4, '0');
    }

    function sortChildItems(a, b) {
        const aOrder = a._order ?? a._index;
        const bOrder = b._order ?? b._index;

        if (
            aOrder !== undefined &&
            bOrder !== undefined &&
            aOrder !== bOrder
        ) {
            return aOrder - bOrder;
        }

        const aTitle = (a.title || '').toLowerCase();
        const bTitle = (b.title || '').toLowerCase();
        if (aTitle < bTitle) return -1;
        if (aTitle > bTitle) return 1;
        return 0;
    }

    async function parseTitle(title, type) {
        const t = { main: title, aside: null };

         
        const ASReg = new RegExp(
            /([0-9]){1,2}: ([AS ]{3})([0-9]{1,}).([0-9]{1,})(.{1,})+/
        );

        switch (type) {
            case 'Unit':
                if (/^[0-9]{1,3}/.test(title)) { // if it starts with numbers
                    t.aside = 'Unit ' + title.split(' ')[0];
                    t.main = title.split(' ').slice(1).join(' ');
                } else if (title.match(ASReg) && title.match(ASReg).length > 0) {
                    const ma = title.match(ASReg);
                    t.aside = `chapter ${ma[1]}`;
                    t.main = ma[5].trim();
                } else if (/^chapter [0-9]{1,3}( |: | : )/gi.test(title)) {
                    t.aside = title.match(/chapter [0-9]{1,3}/gi)[0];
                    t.main = title.replace(/chapter [0-9]{1,3}( |: | : )/gi, '');
                }       
                break;
            case 'Activity':
                if (/^[0-9]{1,3}/.test(title)) { // if it starts with numbers
                    t.aside = 'Activity ' + title.split(' ')[0];
                    t.main = title.split(' ').slice(1).join(' ');
                } else if (title.match(ASReg) && title.match(ASReg).length > 0) {
                    const ma = title.match(ASReg);
                    t.aside = 'introduction';
                    t.main = ma[5].trim();
                } else if (/chapter [0-9]{1,3}( |: | : )/gi.test(title)) {
                    t.aside = 'introduction';
                    t.main = title.replace(/chapter [0-9]{1,3}( |: | : )/gi, '');
                } else if (/^introduction [0-9]{1,3}( |: | : )/gi.test(title)) {
                    t.aside = 'introduction';
                    t.main = title.replace(/^introduction [0-9]{1,3}( |: | : )/gi, '');
                }      
                break;
            case 'Folder': {
                const folderSplit = title.split(':');
                t.aside = folderSplit[0];
                t.main = folderSplit[1];
                break;
            }
        }
        titles = t;
    }

    function getDataTitle(objData) {
        if (/order:/gi.test(objData.keywords))
            return (
                objData.keywords
                    .match(/order:[0-9]{1,3}/gi)[0]
                    .split(':')[1]
                    .padStart(5, '0') +
                ' ' +
                objData.title
            );
        if (
            /^introduction/gi.test(objData.title) ||
            /introduction/gi.test(objData.keywords) ||
            /^book introduction/gi.test(objData.title)
        )
            return '00000 ' + objData.title;
        if (/chapter [0-9]{1,3}( |: | : )/gi.test(objData.title))
            return (
                objData.title.match(/[0-9]{1,3}/g)[0].padStart(4, '0') +
                ' ' +
                objData.title
            );
        if (/appendix/gi.test(objData.title)) return 'ZZZZ ' + objData.title;
        return objData.title.replace(/\b\d{1,}\b/, formatSortNumber);
    }

    onMount(() => {
        setTimeout(() => {
            currentBreadcrumb.subscribe((bc) => {
                if (bc?.breadcrumb && bc.breadcrumb.length > 0) {
                    bc.breadcrumb.forEach((b) => {
                        if (
                            b.id === data.id &&
                            document.getElementById('el_' + data.id)
                        )
                            document
                                .getElementById('el_' + data.id)
                                .scrollIntoView({ behavior: 'smooth' });
                        if (data.items && data.items.indexOf(b.id) > -1) open();
                    });
                }
            });

            parseTitle(data.title, data.catalog);
        }, 500);
    });

    export {
        data,
        activeItem,
        level,
    };
</script>

{#if data}
    {@const shouldBeHeading = !!(level && level >= 1 && level <= 6)}

    <li
        id={'el_' + data.id}
        transition:fade
        class="collection-item book-collection-accordian-item"
        data-id={data.id}
        data-title={getDataTitle(data)}
    >
        <svelte:element
            this={shouldBeHeading ? `h${level}` : 'button'}
            id={'el_btn' + data.id}
            onclick={() => (shouldBeHeading ? null : toggle())}
            class="collection-header interactive"
            style:background-color={`#${(data.hexCodeBackground || '').replaceAll('#', '')}`}
            aria-expanded={shouldBeHeading ? null : !!showSubItems}
            aria-controls={shouldBeHeading ? null : 'el_col_bd_' + data.id}
        >
            <svelte:element
                this={shouldBeHeading ? 'button' : 'div'}
                onclick={() => (shouldBeHeading ? toggle() : null)}
                aria-expanded={shouldBeHeading ? !!showSubItems : null}
                aria-controls={shouldBeHeading ? 'el_col_bd_' + data.id : null}
                class="collection-header-inner {lastActiveItem === data.id
                    ? 'on'
                    : ''} {data.catalog.toLowerCase()} {showSubItems
                        ? 'open'
                        : ''} interactive"
            >
                {#key titles}
                    {#if data.thumbnailURI && data.catalog === 'Title'}
                        {#await resolveStorageUrl(data.thumbnailURI) then resolvedSrc}
                            <img
                                class="icon {data.catalog.toLowerCase()}"
                                src={resolvedSrc}
                                alt=""
                            />
                        {/await}
                    {/if}
                    {#if titles.aside && (!('hasBadge' in data) || data?.hasBadge === true)}
                        <span
                            class={data.catalog.toLowerCase() + ' aside'}
                            style:background-color={`#${(data.hexCodeBadgeBackground || '').replaceAll('#', '')}`}
                            style:color={`#${(data.hexCodeBadgeText || '').replaceAll('#', '')}`}
                            data-originaltitle={data.title}>{titles.aside}</span>
                    {/if}
                    {#if titles.main}
                        <span
                            style={data?.hexCodeText ? 'color: #' + data.hexCodeText.replaceAll('#', '') : ''}
                        >{titles.main}</span>
                    {/if}
                    <div class="spacer"></div>
                    {#if data.catalog === 'Title' || data.catalog === 'Unit' || data.catalog === 'Activity' || data.catalog === 'Folder'}
                        <i 
                            class="material-icons"
                            aria-hidden="true"
                            style={data?.overrideArrowHexCode ? 'color: #' + data.overrideArrowHexCode.replaceAll('#', '') : (data?.hexCodeBadgeBackground ? 'color: #' + data.hexCodeBadgeBackground.replaceAll('#', '') : '')}
                        >
                            {showSubItems ? 'expand_less' : 'expand_more'}
                        </i>
                    {/if}
                {/key}
            </svelte:element>
        </svelte:element>

        <div
            id={'el_col_bd_' + data.id}
            class="collection-body"
            style={showSubItems ? '' : 'display:none;'}
        >
            {#if subItems?.length > 0}
                {#if data.catalog === 'Activity' || data.catalog === 'Folder' || data.keywords.indexOf('Activity Page') > -1}
                    <ResourceHub items={subItems} disabled={!hasAccess} level={level} hiddenIds={currentHiddenIds} {parentHidden} />
                {:else}
                    <BookCollectionAccordianSection
                        items={subItems}
                        parentID={data.id}
                        level={level !== null ? level + 1 : null}
                    />
                {/if}
            {:else}
                <div class="loader">
                    <img src="/ui/loader.svg" alt="Loading icon" />
                </div>
            {/if}
        </div>
    </li>
{/if}
